import * as React from 'react';
import './relationshipModal.scss';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { useActions } from './relationshipModalActions';
import { SeriesPanelContent } from './seriesPanel';
import { Card } from './searchCard';
import { MultiToggler } from '../../Toggler/multiToggler';
import { SearchInput } from '../../SearchInput/searchInput';
import { NodeType } from '../../../enums/NodeType';
import {
  getItemContentTypeId,
  getItemRelationshipFields,
  getItemStatusId,
  getRelationshipOf,
  getSchema
} from '../../../utils';
import InfiniteLoader from '../../InfiniteLoader/infiniteLoader';
import { Metadata } from '../../../types';
import { RelationshipTypeId } from '../../../enums/RelationshipTypeId';
import { useSelector } from '../../../utils/useSelector';
import { isActiveDocument, isWIP } from '../../../utils/status';
import { useLoader } from '../../Loader/loader';
import { useAlert } from '../../Alert/alert';
import { RelationshipFieldType } from '../../../enums/RelationshipFieldType';
import { getIsChildSelect, getIsMultiSelect } from './relationshipField';
import { ContentType } from '../../../enums/ContentType';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { ItemStatus } from '../../../enums/ItemStatus';

const RelationshipType2Id = {
  [RelationshipFieldType.UNI]: RelationshipTypeId.UNI,
  [RelationshipFieldType.ProjectDerivatives]: RelationshipTypeId.ProjectItem,
  [RelationshipFieldType.Series]: RelationshipTypeId.Series
};

type Props = {
  data: Partial<Metadata>;
  onSave: (
    selectedType: RelationshipFieldType,
    selectedItem: Metadata | Metadata[]
  ) => Promise<any>;
  onClose: () => any;
  setSelType: any;
  selType: RelationshipFieldType;
};

type SearchResp = {
  pages: number;
  data: Metadata[];
};

export const RelationshipModal = (props: Readonly<Props>) => {
  const { data, onClose, setSelType, selType } = props;
  const itemSchema = useSelector(s =>
    getSchema(s.planning.selectedWorkspace?.schemas, NodeType.Item)
  );

  const actions = useAlert({ ...useActions(), onSave: props.onSave });
  const { isLoading, Loader, message, Alert, onSave, searchItems } = useLoader(
    useAlert(actions)
  );

  const scrollContainer = React.useRef(null);
  const [isInfiniteScrolling, setIsInfiniteScrolling] = React.useState(false);
  const [selCards, setSelCards] = React.useState<Metadata[]>([]);
  const [searchResult, setSearchResult] = React.useState<SearchResp>(null);
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [selectedRefId, setSelectedRefId] = React.useState<string>(null);

  const pushSelCards = (card: Metadata) => {
    if (getIsMultiSelect(data, selType) && selCards?.length > 0) {
      setSelCards([...selCards, card]);
    } else {
      setSelCards([card]);
    }
  };

  const removeSelCards = (card: Metadata) => {
    setSelCards(selCards?.filter(c => c.props.refId !== card.props.refId));
  };

  const isDisabled = React.useCallback(
    (type: RelationshipFieldType) => {
      const itemStatusId = getItemStatusId(data);
      const itemId = data?.props?.itemId;
      const itemContentTypeId = getItemContentTypeId(data);
      switch (type) {
        case RelationshipFieldType.UNI:
          return (
            !isWIP(itemStatusId) ||
            getRelationshipOf(data, [
              RelationshipTypeId.UNI,
              RelationshipTypeId.PerspectiveContextualization,
              RelationshipTypeId.VariantContextualization
            ]).length > 0 ||
            itemContentTypeId == ContentType.Executive_FastStart
          );
        case RelationshipFieldType.ProjectDerivatives:
          // Disabled on Planning Projects/Parents
          if (!itemId && getIsChildSelect(data, type)) {
            return true;
          }
          if (
            !itemId &&
            getRelationshipOf(data, [RelationshipTypeId.ProjectItem]).length > 0
          ) {
            return true;
          }
          return !!itemId && !isActiveDocument(itemStatusId);
        case RelationshipFieldType.Series:
          if (
            [ItemStatus.Removed, ItemStatus.Not_Pursued].includes(itemStatusId)
          ) {
            return true;
          }
      }
      return false;
    },
    [data]
  );

  const options = React.useMemo(
    () => [
      RelationshipFieldType.UNI,
      RelationshipFieldType.ProjectDerivatives,
      RelationshipFieldType.Series
    ],
    []
  );

  React.useEffect(() => {
    if (!message && !isLoading && (!selType || isDisabled(selType))) {
      for (const t of options) {
        if (!isDisabled(t)) {
          setSelType(t);
          break;
        }
      }
    }
  }, [isDisabled, options, selType, isLoading, message]);

  const isSaveDisabled: boolean =
    isLoading ||
    (selType === RelationshipFieldType.Series
      ? !(
          data?.props?.series?.[0] ||
          (selCards?.length > 1 &&
            selCards?.some(c => c.props.refId === data?.props?.refId))
        )
      : false);

  React.useEffect(() => {
    if (selType == null || !data?.props) return;

    doSearch();
    let initialCards: Metadata[] = [];

    // ET: can be extended later to allow switching between different series
    let selectedRef: string | null = selectedRefId;
    if (selType == RelationshipFieldType.Series && !selectedRefId) {
      selectedRef = data?.props?.series?.[0]?.props?.refId ?? null;
      setSelectedRefId(selectedRef);
    }

    const { getParents, getChildren } = getItemRelationshipFields(
      itemSchema,
      selType
    );

    if (getIsChildSelect(data, selType)) {
      initialCards = getChildren(data, selectedRef) ?? [];
    } else {
      initialCards = getParents(data) ?? [];
    }

    if (selType === RelationshipFieldType.Series && data?.props?.refId) {
      const hasCurrentItem = initialCards.some(
        (c: Metadata) => c.props.refId === data.props.refId
      );
      if (!hasCurrentItem) {
        initialCards = [data as Metadata, ...initialCards];
      }
    }
    setSelCards(initialCards);
  }, [selType, data, itemSchema]);

  const doSearch = React.useCallback(() => {
    if (page > 0) setPage(0);
    setSearchResult(null);
    searchItems(data, selType, query, 0).then((r: any) => {
      if (
        selType === RelationshipFieldType.Series &&
        r?.data &&
        data?.props?.refId
      ) {
        const hasCurrentItem = r.data.some(
          (item: Metadata) => item.props.refId === data.props.refId
        );
        if (!hasCurrentItem && query === '') {
          setSearchResult({
            ...r,
            data: [data, ...r.data]
          });
          return;
        }
      }
      setSearchResult(r);
    });
  }, [selType, query, page, data?.props?.refId]);

  React.useEffect(() => {
    if (page > 0) {
      setIsInfiniteScrolling(true);
      actions
        .searchItems(data, selType, query, page)
        .then((r: any) => {
          setSearchResult(prev => {
            if (!prev?.data) return r;

            if (selType === RelationshipFieldType.Series) {
              const firstItem = prev.data[0];
              const keepSelf = firstItem?.props?.refId === data?.props?.refId;

              if (keepSelf) {
                return {
                  ...r,
                  data: [firstItem, ...prev.data.slice(1), ...r.data]
                };
              }
            }

            return {
              ...r,
              data: [...prev.data, ...r.data]
            };
          });
        })
        .finally(() => setIsInfiniteScrolling(false));
    }
  }, [page, selType, data?.props?.refId]);

  return (
    <div className="shared-modal slider-activator">
      <Modal
        id="relationshipModal"
        onClose={onClose}
        height={638}
        width={900}
        header="Set Up Relationships"
        footer={
          <Buttons>
            <Secondary label="Cancel" onClick={onClose} />
            <Primary
              label="Save"
              onClick={async () => {
                let cardsToSave: any[] = [];
                if (selType === RelationshipFieldType.Series) {
                  cardsToSave = selCards?.map((card, index) => {
                    const { series, items, ...cleanProps } = card.props ?? {};
                    return {
                      ...card,
                      props: cleanProps,
                      relationship: {
                        type: 'ITEM_SERIES',
                        props: { order: index + 1 }
                      }
                    };
                  });
                } else {
                  const relationship = {
                    type: 'ITEM_ITEM',
                    props: { relationshipTypeId: RelationshipType2Id[selType] }
                  };
                  cardsToSave = selCards?.map(card => {
                    const { series, items, ...cleanProps } = card.props ?? {};
                    return {
                      ...card,
                      props: cleanProps,
                      relationship
                    };
                  });
                }
                if (getIsChildSelect(data, selType)) {
                  await onSave(selType, cardsToSave);
                } else {
                  await onSave(selType, cardsToSave[0]);
                }
                onClose();
              }}
              disabled={isSaveDisabled}
            />
          </Buttons>
        }
      >
        <div className="modal-panel-header align-center">
          <div>
            <div className="select-relationship">Select Relationship</div>
            <MultiToggler
              options={options}
              checked={selType}
              disabled={isDisabled}
              onChange={setSelType}
            />
          </div>
        </div>
        <div className="modal-panels">
          <div className="modal-panel">
            <Alert />
            <div className="select-link">
              {(!getIsChildSelect(data, selType) ||
                selType == RelationshipFieldType.UNI) &&
              selType !== RelationshipFieldType.Series ? (
                <> Link To {selType?.split(' ')[0] || 'UNI'} Item </>
              ) : (
                <> Link To {selType} </>
              )}
            </div>
            <span className="input-group">
              <input
                id={`relationship-search`}
                className={'form-control'}
                value={query}
                onChange={q => setQuery(q?.target?.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    doSearch();
                  }
                }}
                placeholder="Search"
                onFocus={event => {
                  event.target.setAttribute('autocomplete', 'off');
                }}
              />
              <SearchInput onClick={doSearch} />
            </span>

            <div
              className="search-result-container scrollable"
              ref={scrollContainer}
            >
              <Loader />
              {searchResult?.data?.length == 0 && (
                <div>No Search Results Found</div>
              )}
              {searchResult?.data?.map(r => {
                const isSelected =
                  selCards?.filter(card => card?.props?.refId === r.props.refId)
                    ?.length > 0;

                const itemSeries = r.props?.series?.[0];
                const inOtherSeries =
                  selType === RelationshipFieldType.Series &&
                  itemSeries?.props?.refId &&
                  itemSeries.props.refId !==
                    data.props?.series?.[0]?.props?.refId;

                return (
                  <Card
                    key={r?.props?.refId}
                    cardData={r}
                    highlight={isSelected}
                    disabled={inOtherSeries}
                    onClick={() =>
                      isSelected ? removeSelCards(r) : pushSelCards(r)
                    }
                    pillText={inOtherSeries ? 'In Series' : null}
                    pillHoverText={
                      inOtherSeries
                        ? 'This item is already part of a series.'
                        : null
                    }
                  />
                );
              })}
              {!!searchResult?.pages && searchResult?.pages > page && (
                <InfiniteLoader
                  container={scrollContainer}
                  loadData={() => {
                    setPage(page + 1);
                  }}
                  loading={isInfiniteScrolling}
                />
              )}
            </div>
          </div>
          {selType === RelationshipFieldType.Series && (
            <div className="modal-panel" style={{ flex: 'none', width: 350 }}>
              <SeriesPanelContent
                selCards={selCards}
                setSelCards={setSelCards}
                currentItemRefId={data?.props?.refId}
                onRemove={removeSelCards}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
